/** Response tab — what the platform answered: status, response headers and
 *  the response body. All data comes with the detail payload — no extra fetch.
 *
 * The body goes through the shared `DocumentViewer` (format detection,
 * pretty-print, wrap, copy, download); the status chip and content type ride
 * in its toolbar, the headers sit between toolbar and body.
 */
import { Box, Typography } from '@mui/material';

import type { ApiRequestDetail } from '@/api/apiRequests';
import { DocumentViewer } from '@/components/DocumentViewer';
import { JsonEditorWidget } from '@/components/JsonEditor/JsonEditorWidget';

import { StatusCodeChip } from '../apiRequestFormat';

/** Content-type fallback for bodies that don't start with `<` or `{`/`[`. */
function hintFromContentType(contentType: string | null | undefined) {
  const ct = (contentType ?? '').toLowerCase();
  if (ct.includes('json')) return 'json';
  if (ct.includes('xml')) return 'xml';
  return undefined;
}

export function ResponseTab({ detail: d }: { detail: ApiRequestDetail }) {
  return (
    <DocumentViewer
      text={d.response ?? ''}
      formatHint={hintFromContentType(d.response_content_type)}
      downloadName={`api-request-${d.id}-response`}
      copyMessage="Response copied to clipboard."
      emptyMessage="No response body was recorded for this request."
      leading={
        <>
          <StatusCodeChip code={d.status_code} />
          <Typography variant="body2" color="text.secondary">
            {d.response_content_type || 'unknown content type'}
          </Typography>
        </>
      }
      beforeViewer={
        d.response_headers != null ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              response headers
            </Typography>
            <JsonEditorWidget initialValue={d.response_headers} readOnly mode="view" />
          </Box>
        ) : null
      }
    />
  );
}
